/**
 * Từ vựng hình dạng — **mọi màn dựng từ những hình ở đây**.
 *
 * Luật, chép nguyên của Autopilot và đây là luật quan trọng nhất của cả thư mục:
 *
 * > **Hình dạng nào có ở file này thì DÙNG. Màn cần một hình file này chưa có thì thêm VÀO ĐÂY
 * > trước, rồi mới dùng.**
 *
 * Vì sao gắt: bên Autopilot có **mười chín** bản sao của `rounded-lg border border-neutral-200`
 * rải khắp các màn. Mỗi bản sao đều bào chữa được, và cộng lại chúng chính là cái *"chỗ to chỗ
 * bé"* mà chủ dự án nhìn thấy — một hệ thiết kế không chết vì một quyết định sai, nó chết vì
 * mười chín quyết định nhỏ đúng.
 *
 * Bốn bán kính mang **vai trò**, không được đổi chỗ cho nhau:
 * `card` panel · `tile` ô số trong panel · `btn` nút · `field` ô nhập.
 */

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

import { BtMuiPhai } from "./bieu-tuong.tsx";

/* ═══ Khung trang ═══════════════════════════════════════════════════════ */

/**
 * Mỗi màn mở đầu bằng **đúng một** cái này.
 *
 * Ba khe, theo thứ tự: tiêu đề · trạng thái của cả màn · một dòng bối cảnh **bên dưới**.
 * Bối cảnh nằm **dưới** tiêu đề, không bao giờ ở góc trên phải — đó là lỗi Autopilot mắc ở màn
 * Tổng quan, nơi một khối đồng hồ năm dòng cạnh tranh với cả tiêu đề lẫn con số thật sự cần
 * người đọc.
 */
export function DauTrang({
  tieuDe,
  trangThai,
  moTa,
  chiTiet,
  children,
}: {
  tieuDe: string;
  trangThai?: ReactNode;
  /** **Một dòng.** Dài hơn một dòng thì phần dài thuộc về `chiTiet`. */
  moTa?: ReactNode;
  /**
   * Phần giải thích dài — **gập lại, mở bằng nút "vì sao"**.
   *
   * Lynust 09/09/2026: *"phải đọc quá nhiều mới làm được"*. Lý do của một luật vẫn đáng giữ — nó
   * là thứ ngăn người sau gỡ luật đó ra — nhưng nó **không được đứng giữa Lynust và cái nút**.
   * Vault ghi đúng câu này ở đợt redesign Autopilot: *một người dùng thành thạo ⇒ ưu tiên mật độ,
   * bỏ văn giải thích*.
   */
  chiTiet?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="space-y-1.5">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="min-w-0 text-xl font-semibold tracking-tight text-muc-900 sm:text-2xl">
          {tieuDe}
        </h1>
        {trangThai}
        {/*
          Hiện ở MỌI khổ, chỉ xuống dòng khi hẹp.
          Từng thử ẩn nó trên điện thoại để nhường chỗ cho `<ThanhHanhDong>` — sai: những màn chỉ
          có "Làm mới" hay một link quay lại thì nút **biến mất hẳn** ở khổ hẹp. Một hành động
          nhìn thấy được ở chỗ hơi bất tiện vẫn hơn một hành động không nhìn thấy.
        */}
        {children && <div className="ml-auto flex flex-wrap items-center gap-2">{children}</div>}
      </div>
      {/* `div` chứ không phải `p`: dòng bối cảnh đôi khi là một cặp nhãn/giá trị, và một thẻ khối
          lồng trong `<p>` là HTML không hợp lệ mà trình duyệt âm thầm bẻ lại. */}
      {(moTa || chiTiet) && (
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-xs text-muc-500 sm:text-sm">
          {moTa && <div className="min-w-0 max-w-[70ch]">{moTa}</div>}
          {chiTiet && <ViSao>{chiTiet}</ViSao>}
        </div>
      )}
    </header>
  );
}

/**
 * Nút "vì sao" — mở phần giải thích dài, **đóng sẵn**.
 *
 * Dùng `<details>` của trình duyệt chứ không phải state: nó gập được cả khi JavaScript chưa chạy,
 * và không có một `useState` nào phải giữ đồng bộ.
 */
export function ViSao({ children, nhan = "vì sao" }: { children: ReactNode; nhan?: string }) {
  return (
    <details className="group min-w-0">
      <summary className="bam-tu-do inline-flex cursor-pointer list-none items-center gap-1 rounded-btn px-1.5 py-0.5 text-2xs font-medium text-muc-500 transition-colors duration-100 hover:bg-muc-200 hover:text-muc-800">
        <span
          className="transition-transform duration-150 ease-[var(--ease-ra)] group-open:rotate-90"
          aria-hidden="true"
        >
          ›
        </span>
        {nhan}
      </summary>
      <div className="mt-1.5 max-w-[70ch] border-l-2 border-muc-200 pl-3 text-xs text-muc-500">
        {children}
      </div>
    </details>
  );
}

/**
 * Thanh hành động **dính đáy trên điện thoại** — dành cho hành động chính của một trang DÀI.
 *
 * Lynust 09/09/2026: *"không biết bấm vào đâu"* và *"trên điện thoại thì vỡ"*. Ở khổ hẹp, nút
 * quyết của màn Chi tiết bài nằm sau một thân bài HTML thô cuộn cả màn hình — muốn bấm phải cuộn
 * qua toàn bộ bài, mỗi lần.
 *
 * ⚠️ **Chỉ dùng khi hành động đó KHÔNG cần đầu vào nào.** Ở Chi tiết bài thanh này chỉ mang nút
 * *Duyệt*: hai nút từ chối cần ô lý do ≥10 ký tự, và một nút từ chối tách rời ô lý do hoặc phải
 * mở hộp thoại, hoặc phải cho từ chối không lý do — cái thứ hai làm hỏng vòng học trong im lặng.
 *
 * `sm:hidden`: trên desktop hành động đã nằm cạnh tiêu đề và cả trang nhìn thấy được cùng lúc,
 * nên một thanh dính đáy ở đó chỉ là một thanh nữa che mất nội dung.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ **ĐÂY LÀ BẢN SAO. Nút thật phải nằm SẴN trên trang.**
 *
 * Thanh này `sm:hidden` ⇒ trên desktop nó **không tồn tại**. Đặt một nút *chỉ* ở đây là đặt một
 * nút **vô hình với người dùng chính**, và không có lỗi nào nổ ra để ai biết.
 *
 * Đã xảy ra ngày 10/09/2026 ở **hai màn cùng lúc** — Kết nối và Sản phẩm. Lynust hỏi *"nút lưu ở
 * đâu, nút kiểm tra kết nối ở đâu?"*. Nguyên nhân gốc không phải CSS: tôi **dùng một thành phần
 * mà chưa đọc nó**, và câu cảnh báo cần đọc nằm ngay trong khối chú thích này.
 *
 * `banSaoChoDienThoai` là prop **bắt buộc** chính vì thế: nó không đổi hành vi, nó buộc người
 * viết phải viết ra bằng chữ rằng mình biết đây là bản sao. Một mặc định im lặng ở đây là một
 * cái bẫy dùng đúng một lần rồi bị quên.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function ThanhHanhDong({
  children,
}: {
  children: ReactNode;
  /** Bắt buộc, và bắt buộc là `true`. Xem khối chú thích ngay trên. */
  banSaoChoDienThoai: true;
}) {
  return (
    <div className="day-an-toan sticky bottom-0 z-30 -mx-4 mt-2 flex items-center gap-2 border-t border-muc-300 bg-muc-50/95 px-4 pt-2.5 backdrop-blur sm:hidden">
      {children}
    </div>
  );
}

/**
 * Hàng nút **luôn nhìn thấy**, ở mọi khổ màn hình. Đây là chỗ mặc định cho nút.
 *
 * Có mặt vì `ThanhHanhDong` trông giống thứ này nhưng biến mất trên desktop, và sự nhầm lẫn đó
 * đã xảy ra thật (10/09/2026).
 */
export function HangNut({ children }: { children: ReactNode }) {
  return <div className="mt-3 flex flex-wrap items-center gap-2">{children}</div>;
}

/**
 * Một đường kẻ mảnh có nhãn, mở ra một vùng của trang.
 *
 * Cách còn lại — cho mỗi vùng một card riêng — là thứ làm một màn dài đọc thành **một đống hộp
 * bằng nhau**. Một đường kẻ tốn một dòng và nói được *"một loại thứ khác bắt đầu từ đây"* mà
 * không tự nhận mình là một đối tượng.
 */
export function KeVung({ nhan, children }: { nhan: string; children?: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <h2 className="shrink-0 text-2xs font-semibold uppercase tracking-[0.12em] text-muc-600">
        {nhan}
      </h2>
      <span className="h-px flex-1 bg-muc-300" />
      {children && <div className="shrink-0 text-2xs text-muc-500">{children}</div>}
    </div>
  );
}

/* ═══ Panel ════════════════════════════════════════════════════════════ */

export function The({
  children,
  className,
  id,
  vien = "thuong",
}: {
  children: ReactNode;
  className?: string;
  /** Neo để link trong trang nhảy tới — ví dụ thanh hành động dính đáy trỏ về khối Quyết. */
  id?: string;
  vien?: "thuong" | "canh" | "loi" | "on";
}) {
  const vienMau = {
    thuong: "border-muc-300 bg-muc-0",
    canh: "border-canh-200 bg-canh-50",
    loi: "border-loi-200 bg-loi-50",
    on: "border-on-200 bg-on-50",
  }[vien];
  // `min-w-0`: một card trong flex/grid mà thiếu nó thì nội dung dài đẩy cả hàng tràn ngang,
  // và `truncate` bên trong ngừng hoạt động (AP-35, AP-76).
  return (
    <section id={id} className={clsx("min-w-0 rounded-card border p-4 shadow-card", vienMau, className)}>
      {children}
    </section>
  );
}

export function DauThe({ tieuDe, phu, children }: { tieuDe: string; phu?: ReactNode; children?: ReactNode }) {
  return (
    <div className="mb-3 flex min-w-0 flex-wrap items-baseline justify-between gap-2">
      <div className="min-w-0">
        <h3 className="truncate text-sm font-semibold text-muc-800" title={tieuDe}>
          {tieuDe}
        </h3>
        {phu && <p className="mt-0.5 text-xs text-muc-500">{phu}</p>}
      </div>
      {children}
    </div>
  );
}

/* ═══ Số ═══════════════════════════════════════════════════════════════ */

/**
 * Một ô số bên trong panel — bán kính `tile`, không phải `card`.
 *
 * ⚠️ **`so === null` hiện dấu gạch, KHÔNG hiện 0.** `CLAUDE.md` §5.14: *"chưa đo được" và "bằng
 * 0" là hai chuyện khác nhau, ở mọi màn hình*. Hiện 0 cho một thứ chưa đo được là nói dối bằng
 * một con số, và người xem sẽ tin nó.
 */
export function OSo({
  nhan,
  so,
  ghiChu,
  muc = "thuong",
  to,
}: {
  nhan: string;
  so: number | string | null;
  ghiChu?: ReactNode;
  muc?: "thuong" | "canh" | "loi" | "on";
  to?: boolean;
}) {
  const nen = {
    thuong: "border-muc-200 bg-muc-50",
    canh: "border-canh-200 bg-canh-50",
    loi: "border-loi-200 bg-loi-50",
    on: "border-on-200 bg-on-50",
  }[muc];
  const mauSo = {
    thuong: "text-muc-900",
    canh: "text-canh-700",
    loi: "text-loi-700",
    on: "text-on-700",
  }[muc];

  return (
    <div className={clsx("min-w-0 rounded-tile border px-3 py-2.5", nen)}>
      <div className="truncate text-2xs text-muc-500" title={nhan}>
        {nhan}
      </div>
      <div className={clsx("so-thang-cot mt-0.5 font-semibold", to ? "text-3xl" : "text-2xl", mauSo)}>
        {so === null ? <ChuaDo /> : so}
      </div>
      {ghiChu && <div className="mt-0.5 text-2xs text-muc-500">{ghiChu}</div>}
    </div>
  );
}

/**
 * Dấu cho **"chưa đo được"**.
 *
 * Nó tồn tại riêng thay vì viết `—` rải rác, vì đây là một **khái niệm**, không phải một ký tự:
 * mọi chỗ hiện nó phải hiện giống nhau, và phải có `title` để người xem biết đó không phải lỗi.
 */
export function ChuaDo({ nhan = "chưa đo được" }: { nhan?: string }) {
  return (
    <span className="text-muc-400" title={nhan}>
      —
    </span>
  );
}

/* ═══ Nút ══════════════════════════════════════════════════════════════ */

type KieuNut = "chinh" | "vien" | "nguyHiem" | "phang";

/**
 * ⚠️ **`chinh` là mảng CAM, và mỗi màn có NHIỀU NHẤT MỘT cái.**
 *
 * Cam mang đúng một nghĩa trong cả app — *có việc cần Lynust*. Một màn có hai nút cam là một màn
 * đã không nói gì; lời kêu gọi thứ hai phải là `vien`.
 *
 * Chữ trên cam là **gần đen** (`canh-fg` #222222, 5.00:1), **không phải trắng** — trắng trên
 * #FD5825 chỉ đạt 2.6 và trượt xa AA. Đây là chỗ dễ sai nhất khi chép một bảng màu cam: bản năng
 * là đặt chữ trắng lên mảng màu đậm, và ở sắc này bản năng đó sai.
 *
 * Hover đi một bước **hẹp** có chủ ý: mảng cam mang chữ gần đen, nên đậm hơn nữa là kéo chính cặp
 * đó xuống dưới 4.5.
 */
const KIEU: Record<KieuNut, string> = {
  chinh: "bg-canh-500 text-canh-fg hover:bg-canh-hover", // gần đen trên cam: 5.00
  vien: "bg-muc-0 text-muc-700 ring-1 ring-muc-300 hover:bg-muc-100",
  nguyHiem: "bg-loi-500 text-muc-0 hover:bg-loi-600", // chữ trắng trên đỏ thẫm: 5.91
  phang: "text-muc-600 hover:bg-muc-200",
};

/**
 * Nút.
 *
 * Hai sửa đổi của đợt 20, cả hai trả lời triệu chứng *"bấm rồi không biết đã xong chưa"*:
 *
 * 1. **`active:scale-[0.97]`.** Không có nó thì cú bấm không có phản hồi nào trong khoảng thời
 *    gian trước khi mạng trả lời — người bấm không biết máy đã nghe thấy chưa. Emil:
 *    *"instant feedback, making the UI feel like it is truly listening"*.
 * 2. **`dangChay` GIỮ NGUYÊN bề rộng.** Bản cũ thay `{children}` bằng `"Đang xử lý…"`, nên nút
 *    "Duyệt" đột ngột rộng gấp ba và mọi thứ cạnh nó nhảy chỗ. Giờ chữ gốc **ở nguyên đó**, mờ
 *    đi, và con quay chồng lên trên.
 *
 * ⚠️ `transition` trần đã bị thay bằng danh sách thuộc tính tường minh — `transition` một mình
 * tương đương `transition: all`, nó chạy cả `background-color`, `color`, `border`, và mỗi cái là
 * một lần tính lại không ai yêu cầu.
 */
export function Nut({
  kieu = "vien",
  dangChay,
  children,
  className,
  ...con
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { kieu?: KieuNut; dangChay?: boolean }) {
  return (
    <button
      {...con}
      disabled={con.disabled || dangChay}
      aria-busy={dangChay || undefined}
      className={clsx(
        "relative rounded-btn px-3 py-1.5 text-sm font-medium",
        "transition-[transform,background-color,box-shadow] duration-[var(--nhip-bam)] ease-[var(--ease-ra)]",
        // Chỉ nhún khi còn bấm được. Một nút disabled mà vẫn nhún là nói dối bằng chuyển động.
        "active:enabled:scale-[0.97]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        KIEU[kieu],
        className,
      )}
    >
      {/* Chữ gốc ở NGUYÊN chỗ cũ, chỉ mờ đi — nút không đổi bề rộng, không đẩy hàng bên cạnh. */}
      <span className={clsx("transition-opacity duration-100", dangChay && "opacity-0")}>
        {children}
      </span>
      {dangChay && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span
            className="quay-nhanh h-4 w-4 rounded-full border-2 border-current border-t-transparent opacity-70"
            aria-hidden="true"
          />
          <span className="sr-only">Đang xử lý…</span>
        </span>
      )}
    </button>
  );
}

/* ═══ Chip và huy hiệu ═════════════════════════════════════════════════ */

export function Chip({
  children,
  muc = "thuong",
  title,
}: {
  children: ReactNode;
  muc?: "thuong" | "canh" | "loi" | "on" | "dam";
  title?: string;
}) {
  const mau = {
    thuong: "bg-muc-200 text-muc-700",
    canh: "bg-canh-50 text-canh-700 ring-1 ring-canh-200",
    loi: "bg-loi-50 text-loi-700 ring-1 ring-loi-200",
    on: "bg-on-50 text-on-700 ring-1 ring-on-200",
    dam: "bg-muc-900 text-muc-0",
  }[muc];
  return (
    <span
      title={title}
      className={clsx("inline-block shrink-0 rounded-btn px-2 py-0.5 text-2xs font-medium", mau)}
    >
      {children}
    </span>
  );
}

/* ═══ Ô nhập ═══════════════════════════════════════════════════════════ */

export const lopO =
  "w-full rounded-field border border-muc-300 bg-muc-0 px-3 py-2 text-sm text-muc-800 outline-none transition-[border-color,box-shadow] duration-100 focus:border-muc-500 focus:ring-2 focus:ring-muc-200";

export function Truong({
  nhan,
  goiY,
  loi,
  children,
}: {
  nhan: string;
  goiY?: ReactNode;
  loi?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="block min-w-0">
      <span className="text-sm font-medium text-muc-700">{nhan}</span>
      <div className="mt-1">{children}</div>
      {loi ? (
        <p className="mt-1 text-xs text-loi-600">{loi}</p>
      ) : (
        goiY && <p className="mt-1 text-xs text-muc-500">{goiY}</p>
      )}
    </label>
  );
}

/* ═══ Ba trạng thái ════════════════════════════════════════════════════ */

export function DangTai({ children = "Đang tải…" }: { children?: ReactNode }) {
  return (
    <div className="animate-pulse space-y-3" aria-busy="true">
      <div className="h-4 w-1/3 rounded-btn bg-muc-200" />
      <div className="h-20 rounded-tile bg-muc-100" />
      <div className="h-20 rounded-tile bg-muc-100" />
      <span className="sr-only">{children}</span>
    </div>
  );
}

export function Rong({ tieuDe, children }: { tieuDe: string; children?: ReactNode }) {
  return (
    <div className="rounded-card border border-dashed border-muc-300 p-8 text-center">
      <p className="font-medium text-muc-700">{tieuDe}</p>
      {children && <div className="mt-1 text-sm text-muc-500">{children}</div>}
    </div>
  );
}

export function Bao({
  muc = "canh",
  tieuDe,
  children,
}: {
  muc?: "canh" | "loi" | "on" | "thuong";
  tieuDe?: string;
  children: ReactNode;
}) {
  const mau = {
    canh: "border-canh-200 bg-canh-50 text-canh-700",
    loi: "border-loi-200 bg-loi-50 text-loi-700",
    on: "border-on-200 bg-on-50 text-on-700",
    thuong: "border-muc-300 bg-muc-100 text-muc-600",
  }[muc];
  return (
    <div className={clsx("rounded-card border p-4 text-sm", mau)} role={muc === "loi" ? "alert" : undefined}>
      {tieuDe && <p className="font-semibold">{tieuDe}</p>}
      <div className={clsx(tieuDe && "mt-1")}>{children}</div>
    </div>
  );
}

/* ═══ Danh sách hàng ═══════════════════════════════════════════════════ */

export function DanhSach({ children }: { children: ReactNode }) {
  return (
    <ul className="min-w-0 divide-y divide-muc-200 overflow-hidden rounded-card border border-muc-300 bg-muc-0 shadow-card">
      {children}
    </ul>
  );
}

export function Hang({ children, className }: { children: ReactNode; className?: string }) {
  return <li className={clsx("min-w-0", className)}>{children}</li>;
}

/* ═══ Khối gập được ════════════════════════════════════════════════════ */

/**
 * Một vùng của trang, **gập được**, có nhớ trạng thái.
 *
 * Khác `KeVung` ở chỗ nó **cất được nội dung đi**. Một màn dài như Cài đặt có bốn nhóm, và người
 * mở nó ra thường chỉ cần một nhóm — bắt cuộn qua ba nhóm kia mỗi lần là thuế thu hằng ngày.
 *
 * `id` là khoá để nhớ trạng thái mở qua các lần tải. Bỏ trống thì không nhớ — đúng cho một khối
 * mà mặc định luôn là câu trả lời đúng.
 *
 * `localStorage` bọc try/catch: chế độ riêng tư của trình duyệt **ném** ở cả đọc lẫn ghi, và một
 * dashboard không được sập vì không nhớ nổi một cái mũi tên.
 */
export function Khoi({
  nhan,
  tomTat,
  moSan = false,
  id,
  children,
}: {
  nhan: string;
  tomTat?: ReactNode;
  moSan?: boolean;
  id?: string;
  children: ReactNode;
}) {
  const khoaLuu = id ? `autoime.khoi.${id}` : null;
  const [mo, datMo] = useState(() => {
    if (!khoaLuu) return moSan;
    try {
      const v = localStorage.getItem(khoaLuu);
      return v === null ? moSan : v === "1";
    } catch {
      return moSan;
    }
  });

  const bat = () => {
    datMo((v) => {
      const ke = !v;
      try {
        if (khoaLuu) localStorage.setItem(khoaLuu, ke ? "1" : "0");
      } catch {
        /* chế độ riêng tư — lựa chọn chỉ sống trong phiên này */
      }
      return ke;
    });
  };

  const idPanel = `khoi-${id ?? nhan.replace(/\s+/g, "-")}`;

  return (
    <section className="min-w-0 space-y-3">
      <h2>
        <button
          type="button"
          onClick={bat}
          aria-expanded={mo}
          aria-controls={idPanel}
          className="group flex w-full items-center gap-2.5 py-1 text-left"
        >
          <BtMuiPhai
            className={clsx(
              "h-3.5 w-3.5 shrink-0 text-muc-400 transition-transform",
              mo && "rotate-90",
            )}
          />
          <span className="shrink-0 text-2xs font-semibold uppercase tracking-[0.12em] text-muc-600 group-hover:text-muc-900">
            {nhan}
          </span>
          <span className="h-px min-w-6 flex-1 bg-muc-200" />
          {tomTat != null && (
            <span className="min-w-0 max-w-[60%] truncate text-2xs text-muc-500">{tomTat}</span>
          )}
        </button>
      </h2>
      {mo && (
        <div id={idPanel} className="min-w-0 space-y-3">
          {children}
        </div>
      )}
    </section>
  );
}

/* ═══ Số đọc — nhỏ hơn OSo, nằm thành lưới ════════════════════════════ */

/**
 * Một **số đọc**: nhỏ hơn `OSo`, dùng khi có bốn tới sáu con số cạnh nhau.
 *
 * `canAnh` bật viền cam — đây là chỗ **duy nhất** một ô số được mang màu cam, và nó mang đúng
 * nghĩa của cam trong cả app: *có việc cần Lynust*. Đừng dùng nó cho một con số chỉ "đáng chú ý".
 *
 * `dangTai` hiện dấu gạch mờ chứ không hiện 0 — cùng luật với `ChuaDo`.
 */
export function Doc({
  nhan,
  giaTri,
  ghiChu,
  den,
  dangTai,
  canAnh,
  sacThai,
}: {
  nhan: string;
  giaTri: ReactNode;
  ghiChu?: ReactNode;
  den?: string;
  dangTai?: boolean;
  canAnh?: boolean;
  sacThai?: "tot" | "xau";
}) {
  const mau = canAnh
    ? "text-canh-600"
    : sacThai === "tot"
      ? "text-on-600"
      : sacThai === "xau"
        ? "text-loi-600"
        : "text-muc-900";

  const than = (
    <div
      className={clsx(
        "h-full min-w-0 rounded-tile border bg-muc-0 px-3.5 py-3",
        canAnh ? "border-canh-200 bg-canh-50" : "border-muc-200",
        den && "transition-colors hover:border-muc-300",
      )}
    >
      <div className={clsx("so-thang-cot text-lg font-semibold sm:text-xl", mau)}>
        {dangTai ? <span className="text-muc-300">—</span> : giaTri}
      </div>
      <div className="mt-0.5 truncate text-2xs text-muc-500" title={nhan}>
        {nhan}
      </div>
      {ghiChu && <div className="mt-1 text-2xs text-muc-500">{ghiChu}</div>}
    </div>
  );

  return den ? (
    <Link to={den} className="block min-w-0">
      {than}
    </Link>
  ) : (
    than
  );
}

/** Một hàng số đọc. **Hai cái một hàng trên điện thoại**, tới sáu trên máy tính. */
export function LuoiDoc({ children, cot = 4 }: { children: ReactNode; cot?: 3 | 4 | 5 | 6 }) {
  const rong = { 3: "sm:grid-cols-3", 4: "sm:grid-cols-4", 5: "sm:grid-cols-5", 6: "sm:grid-cols-6" }[
    cot
  ];
  return <div className={clsx("grid min-w-0 grid-cols-2 gap-2.5", rong)}>{children}</div>;
}

/* ═══ Bảng ═════════════════════════════════════════════════════════════ */

/**
 * Bảng rộng **tự cuộn bên trong chính nó**.
 *
 * Thân trang **không bao giờ được cuộn ngang**: trên máy tính nó làm layout trông hỏng, trên điện
 * thoại nó giấu mất thanh điều hướng. Mọi bảng trong app đi qua đây để luật đó không thể bị quên ở
 * một màn. `cuon-ngang` vẽ cái bóng nói *"còn nữa ở bên phải"*.
 */
export function BocBang({ children, rongToiThieu = "48rem" }: { children: ReactNode; rongToiThieu?: string }) {
  return (
    <div className="cuon-ngang min-w-0 overflow-x-auto rounded-card border border-muc-300 bg-muc-0 shadow-card">
      <table className="w-full text-xs sm:text-sm" style={{ minWidth: rongToiThieu }}>
        {children}
      </table>
    </div>
  );
}

export function DauBang({ children }: { children: ReactNode }) {
  return (
    <thead className="border-b border-muc-300 bg-muc-100 text-2xs uppercase tracking-[0.08em] text-muc-600">
      {children}
    </thead>
  );
}

export function OTieuDe({ children, canh = "trai" }: { children?: ReactNode; canh?: "trai" | "phai" }) {
  return (
    <th
      className={clsx(
        "whitespace-nowrap px-3 py-2 font-medium",
        canh === "phai" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}

export function ThanBang({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-muc-200">{children}</tbody>;
}

/**
 * Một con số trong ô bảng.
 *
 * `so-thang-cot` để chữ số thẳng hàng. Thiếu nó thì "1.204" và "998" rộng khác nhau, và một cột
 * tiền đọc ra lởm chởm — làm khó việc so hai hàng, mà so hai hàng là **lý do duy nhất** cột đó tồn tại.
 */
export function So({
  children,
  sacThai,
  title,
}: {
  children: ReactNode;
  sacThai?: "tot" | "xau" | "mo" | "nhan";
  title?: string;
}) {
  const mau =
    sacThai === "tot"
      ? "text-on-600"
      : sacThai === "xau"
        ? "text-loi-600"
        : sacThai === "mo"
          ? "text-muc-500"
          : sacThai === "nhan"
            ? "font-medium text-canh-600"
            : "text-muc-800";

  return (
    <td className={clsx("so-thang-cot px-3 py-2 text-right", mau)} title={title}>
      {children}
    </td>
  );
}

/** Ô chữ thường trong bảng — để không ai phải tự viết `px-3 py-2` lần thứ hai mươi. */
export function OBang({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={clsx("min-w-0 px-3 py-2", className)}>{children}</td>;
}

/* ═══ Công tắc ═════════════════════════════════════════════════════════ */

/**
 * Công tắc bật/tắt.
 *
 * Dùng cho thứ **lật một cờ**. Một thao tác lớn hơn — chạy bù vòng học, đăng ngay — vẫn là một
 * **nút có động từ**, và trạng thái hiện tại nằm cạnh nó bằng `HuyHieu`. Động từ và trạng thái là
 * hai thứ khác nhau và không bao giờ được dùng chung một nhãn.
 *
 * `role="switch"` được CSS loại khỏi luật cao tối thiểu 44px trên điện thoại — nếu không nó bị kéo
 * dài thành viên thuốc.
 */
export function CongTac({
  bat,
  doi,
  khoa = false,
  dangChay = false,
  nhanDocMan,
  hienChu = true,
  co = "vua",
  title,
}: {
  bat: boolean;
  doi: (ke: boolean) => void;
  khoa?: boolean;
  dangChay?: boolean;
  /** Công tắc này điều khiển cái gì — cho trình đọc màn hình, khi không có nhãn nhìn thấy được. */
  nhanDocMan?: string;
  hienChu?: boolean;
  co?: "nho" | "vua";
  title?: string;
}) {
  const nho = co === "nho";
  return (
    <span className="inline-flex shrink-0 items-center gap-2" title={title}>
      <button
        type="button"
        role="switch"
        aria-checked={bat}
        aria-label={nhanDocMan}
        disabled={khoa}
        onClick={() => doi(!bat)}
        className={clsx(
          "relative inline-flex shrink-0 items-center rounded-full border transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-50",
          nho ? "h-5 w-9" : "h-6 w-11",
          bat ? "border-canh-500 bg-canh-500" : "border-muc-300 bg-muc-300",
        )}
      >
        <span
          className={clsx(
            "inline-block transform rounded-full bg-muc-0 shadow-sm transition-transform",
            nho ? "h-3.5 w-3.5" : "h-4 w-4",
            bat ? (nho ? "translate-x-4" : "translate-x-6") : "translate-x-1",
          )}
        />
      </button>
      {hienChu && (
        <span
          className={clsx(
            "so-thang-cot font-medium",
            nho ? "text-2xs" : "text-xs",
            bat ? "text-canh-600" : "text-muc-500",
          )}
        >
          {dangChay ? "…" : bat ? "Đang bật" : "Đang tắt"}
        </span>
      )}
    </span>
  );
}

/** Tiêu đề và giải thích bên trái, một công tắc bên phải. */
export function HangCongTac({
  tieuDe,
  goiY,
  bat,
  doi,
  khoa = false,
  dangChay = false,
  titleCongTac,
}: {
  tieuDe: string;
  goiY?: ReactNode;
  bat: boolean;
  doi: (ke: boolean) => void;
  khoa?: boolean;
  dangChay?: boolean;
  titleCongTac?: string;
}) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-sm font-medium text-muc-800">{tieuDe}</h3>
        {goiY && <div className="mt-0.5 text-xs text-muc-500">{goiY}</div>}
      </div>
      <CongTac
        bat={bat}
        doi={doi}
        khoa={khoa}
        dangChay={dangChay}
        nhanDocMan={tieuDe}
        title={titleCongTac}
      />
    </div>
  );
}

/* ═══ Huy hiệu trạng thái ══════════════════════════════════════════════ */

/**
 * Trạng thái **hiện tại**, dạng nhãn chứ không phải nút bấm.
 *
 * Cho những chỗ mà cái nút bên cạnh nói điều **sắp xảy ra** ("Bật cron"), khiến điều **đang xảy
 * ra** không ai nói.
 *
 * Teal, **không phải cam**. Trong bảng màu này cam mang đúng một nghĩa — có việc cần Lynust — mà
 * một công tắc đang BẬT là điều ngược lại: máy đang tự chạy. Teal là sắc "vẫn đang chạy", và
 * #3FABAF dưới chữ `on-900` đo được 4.87.
 */
export function HuyHieu({
  bat,
  chuBat = "Đang bật",
  chuTat = "Đang tắt",
}: {
  bat: boolean;
  chuBat?: string;
  chuTat?: string;
}) {
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-2xs font-semibold",
        bat ? "bg-on-500 text-on-900" : "bg-muc-200 text-muc-600",
      )}
    >
      <span className={clsx("h-1.5 w-1.5 rounded-full", bat ? "bg-on-900" : "bg-muc-500")} />
      {bat ? chuBat : chuTat}
    </span>
  );
}

/**
 * Huy hiệu chế độ đăng, đứng cạnh tiêu đề trang.
 *
 * `live` là trạng thái **tiêu tiền và không hoàn tác được**, nên đây là chỗ DUY NHẤT màu đỏ xuất
 * hiện như một **trạng thái** chứ không phải một lỗi.
 *
 * Bên Autopilot nó từng dùng cùng màu xanh với "đăng thành công", khiến dữ kiện hệ trọng nhất
 * trong cả app trông như một tin tốt thường ngày.
 */
export function HuyHieuLive({ live }: { live: boolean }) {
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs font-semibold uppercase tracking-[0.06em]",
        live ? "border border-loi-200 bg-loi-50 text-loi-600" : "bg-muc-200 text-muc-600",
      )}
    >
      {live && <span className="h-1.5 w-1.5 rounded-full bg-loi-500" />}
      {live ? "Đang đăng thật" : "Chế độ thử (dry)"}
    </span>
  );
}
